const BUTTON_SELECTOR = '.btn-outline-currentColor, .btn-currentColor';

const TRANSPARENT_VALUES = new Set([
  'transparent',
  'rgba(0, 0, 0, 0)',
  'rgba(0,0,0,0)',
  'rgb(0, 0, 0)',
  'rgb(0,0,0)',
]);

const isTransparent = (value) => {
  if (!value) {
    return true;
  }

  const normalized = value.trim().toLowerCase();

  if (TRANSPARENT_VALUES.has(normalized)) {
    return true;
  }

  if (normalized.startsWith('rgba(') && normalized.endsWith(')')) {
    const alpha = normalized.split(',').pop()?.replace(')', '').trim();
    return alpha === '0' || alpha === '0.0' || alpha === '0.00';
  }

  return false;
};

const findEffectiveBackground = (element) => {
  let current = element?.parentElement || null;

  while (current) {
    const styles = window.getComputedStyle(current);
    const bgColor = styles.backgroundColor;
    const bgImage = styles.backgroundImage;

    if ((bgColor && !isTransparent(bgColor)) || (bgImage && bgImage !== 'none')) {
      return {
        element: current,
        backgroundColor: bgColor,
        hasImage: bgImage && bgImage !== 'none',
      };
    }

    current = current.parentElement;

    if (!current || current === document.body) {
      const bodyStyles = window.getComputedStyle(document.body);
      return {
        element: document.body,
        backgroundColor: bodyStyles.backgroundColor || 'white',
        hasImage: bodyStyles.backgroundImage && bodyStyles.backgroundImage !== 'none',
      };
    }
  }

  return {
    element: document.body,
    backgroundColor: 'white',
    hasImage: false,
  };
};

const rgbRegex =
  /rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|1|0?\.\d+))?\s*\)/i;

const getContrastColor = (backgroundColor) => {
  const match = backgroundColor?.match(rgbRegex);

  if (!match) {
    return 'rgb(17, 54, 52)';
  }

  const r = Number.parseInt(match[1], 10);
  const g = Number.parseInt(match[2], 10);
  const b = Number.parseInt(match[3], 10);

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  if (luminance > 0.5) {
    return 'rgb(17, 54, 52)';
  }

  return 'rgb(222, 238, 198)';
};

const processButtons = (root = document) => {
  if (!root) {
    return;
  }

  const buttons = root.matches?.(BUTTON_SELECTOR)
    ? [root]
    : Array.from(root.querySelectorAll?.(BUTTON_SELECTOR) || []);

  // READ-Phase: Alle Backgrounds sammeln
  const buttonStyles = buttons.map((button) => ({
    button,
    backgroundColor: findEffectiveBackground(button).backgroundColor,
  }));

  // WRITE-Phase: Alle Styles setzen
  buttonStyles.forEach(({ button, backgroundColor }) => {
    const contrastColor = getContrastColor(backgroundColor);
    button.style.setProperty('--parent-bg-color', backgroundColor);
    button.style.setProperty('--btn-context-color', contrastColor);
  });
};

export function initButtonContextStyler() {
  const init = () => {
    processButtons(document);

    if ('MutationObserver' in window) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1) {
                processButtons(node);
              }
            });
          } else if (
            mutation.type === 'attributes' &&
            mutation.target instanceof Element &&
            mutation.target.matches(BUTTON_SELECTOR)
          ) {
            processButtons(mutation.target);
          }
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('load', () => processButtons(document));
}
